import _cloneDeep from 'lodash/cloneDeep';

import { tierPayloadToForm } from 'containers/Campaign/Referral/TierLadder/payload';
import {
  OutcomeSettings,
  withOutcomeSettingsDefaults
} from 'containers/Campaign/outcomeSettingsDefaults';

interface INormalizeOptions {
  // Supplied only where the popup copy is actually editable, so a tenant without the feature never
  // grows settings it has no screen for.
  outcomeSettingsDefaults?: OutcomeSettings;
}

export const campaignNormalizeData = (campaign, options: INormalizeOptions = {}) => {
  const clonedCampaign = _cloneDeep(campaign);

  if (clonedCampaign?.rules?.length > 0) {
    const ruleNameToFind = 'Push Notification Rule for Instant Outcome Transaction Issue';

    const sendPushNotificationRule = clonedCampaign.rules.find(
      rule => rule?.name === ruleNameToFind
    );

    clonedCampaign.pushNotificationRule = {
      name: ruleNameToFind,
      ruleId: sendPushNotificationRule?.id || null,
      send_notification: sendPushNotificationRule?.send_notification || false,
      outcome_options: {
        selected_media_type_id:
          sendPushNotificationRule?.outcome_options?.selected_media_type_id || null,
        custom_fields: sendPushNotificationRule?.outcome_options?.custom_fields || {}
      },
      event_params: {
        campaign_id: clonedCampaign.id
      }
    };

    // Extract send_notification_on_auto_redeem to root level (prefer campaign value over rule value)
    clonedCampaign.send_notification_on_auto_redeem =
      clonedCampaign.send_notification_on_auto_redeem ??
      sendPushNotificationRule?.send_notification_on_auto_redeem ??
      false;

    if (sendPushNotificationRule) {
      clonedCampaign.rules = clonedCampaign.rules.filter(rule => rule.name !== ruleNameToFind);
    }
  }

  if (clonedCampaign.campaign_type === 'stamp') {
    if (clonedCampaign.outcomes && clonedCampaign.outcomes.length > 0) {
      clonedCampaign.initialSlottedRewards = clonedCampaign.outcomes; // prepare for initial reward, it will get preprocessed inside campaign mechanic page
    }
  }

  if (clonedCampaign.campaign_type === 'game') {
    if (clonedCampaign.game_type === 'survey' || clonedCampaign.game_type === 'quiz') {
      clonedCampaign.campaign_type = clonedCampaign.game_type;
    }

    // A claw campaign is given its popup copy when the game type is picked in the create modal, so
    // one that never went through it — authored over the API, imported, or created before the popup
    // existed — reaches the form with every Outcome Settings tab blank and no way to fill them. The
    // gaps close here, where the API payload becomes form data, so the form starts complete.
    if (clonedCampaign.game_type === 'claw_machine' && options.outcomeSettingsDefaults) {
      Object.keys(clonedCampaign)
        .filter(key => key.startsWith('display_properties_'))
        .forEach(key => {
          clonedCampaign[key] = {
            ...clonedCampaign[key],
            outcome_settings: withOutcomeSettingsDefaults(
              clonedCampaign[key]?.outcome_settings,
              options.outcomeSettingsDefaults as OutcomeSettings
            )
          };
        });
    }
  }

  if (clonedCampaign.campaign_type === 'invite') {
    clonedCampaign.properties = clonedCampaign.properties ?? {};
    if (clonedCampaign.tier_based_invite === true) {
      // Hydrate properties.reward_mode + properties.tier_ladder.tiers[] from
      // the top-level wire shape (tiers[] + transaction_rule + blueprint-shaped
      // tier outcomes) so the redux-form Referral UI renders correctly.
      return tierPayloadToForm(clonedCampaign);
    }
    if (!clonedCampaign.properties.reward_mode) {
      clonedCampaign.properties.reward_mode = 'fixed';
    }
  }

  if (clonedCampaign.campaign_type === 'streak') {
    clonedCampaign.temp = {
      interval_id: clonedCampaign.streak_intervals[0]?.id,
      streak_period: clonedCampaign.streak_intervals[0]?.streak_period,
      trigger_type: clonedCampaign.streak_campaign_mechanics?.streak_type,
      streak_unit: clonedCampaign.streak_intervals[0]?.streak_unit,
      event_count: clonedCampaign.streak_intervals[0]?.event_count,
      streak_mode: clonedCampaign.streak_campaign_mechanics?.streak_mode,
      target_spend: clonedCampaign.streak_intervals[0]?.target_spend
    };
    clonedCampaign.outcomes = clonedCampaign.streak_intervals[0]?.outcomes;
    clonedCampaign.milestones = clonedCampaign.streak_intervals;
  }

  return clonedCampaign;
};
